import { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import TopBar from "@/components/topbar";
import { useSheetStore } from "@/zustand/sheetStore";
import BinCard from "@/components/bin-card";
import { useGetBIN, useGetCardSubscriptions } from "@/hooks/use-queries";
import { groupPlansByBin } from "@/lib/subscription";
import { cardStyleFor } from "@/lib/card-style";

// ── Types ────────────────────────────────────────────────────────────────────

export type BinCardData = {
  id: number;
  name: string;
  image: string;
  price: number;
  defaultPrice?: number;
  bin: string;
  binValue: string;
  tierLabel?: string;
  payBadgeBackground?: string;
  network: string;
  cardType: string;
  provider: string;
  needDepositForActiveCard: boolean;
  needCardHolder: boolean;
  minDepositAmount: number;
  maxDepositAmount: number;
  topUpFee: number;
  background?: string;
  cardBorder?: string;
};

// Display order for the shop grid.
const PROVIDER_ORDER = ["ptp", "wsb", "int"];

function supportText(provider: string) {
  const hi = (text: string) => (
    <strong style={{ color: "#E1E1E1" }}>{text}</strong>
  );
  if (provider === "int")
    return {
      supportTitle: "Supported Usage Scenarios",
      supportBody: (
        <>
          The card {hi("DOES NOT")} currently support Uber, Petrol stations,
          Talabat, Hermes Amsterdam, Hermes Paris, and Iberia and a few other
          merchants.
        </>
      ),
    };
  return {
    supportTitle: "Supported Usage Scenarios",
    supportBody: (
      <>
        The card {hi("SUPPORTS")} Uber, petrol stations, YouTube, AliExpress,
        Amazon, Iberia, Talabat.
      </>
    ),
  };
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const CardSkeleton = () => (
  <div className="bg-dark-card-3 rounded-2xl border border-[#3A3A3A] p-3 shadow-sm sm:p-4">
    <div className="h-[180px] animate-pulse rounded-xl bg-gray-200" />
    <div className="mt-3 space-y-2">
      <div className="h-2.5 w-28 animate-pulse rounded-full bg-gray-200" />
      <div className="h-2.5 w-full animate-pulse rounded-full bg-gray-200" />
      <div className="h-2.5 w-3/4 animate-pulse rounded-full bg-gray-200" />
    </div>
    <div className="mt-3 flex items-center justify-between border-t border-[#3A3A3A] pt-3">
      <div className="h-5 w-24 animate-pulse rounded-full bg-gray-200" />
      <div className="size-4 animate-pulse rounded-full bg-gray-200" />
    </div>
  </div>
);

// ── Shop ─────────────────────────────────────────────────────────────────────

const Shop = () => {
  const { openSheet } = useSheetStore();
  const { data: bins, isLoading: isLoadingBins } = useGetBIN();
  const { data: subscriptions, isLoading: isLoadingPlans } =
    useGetCardSubscriptions();
  const isLoading = isLoadingBins || isLoadingPlans;

  const plansByBin = groupPlansByBin(subscriptions);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleClick = (bin: any) => {
    if (bin.provider === "int") {
      openSheet(
        "createSapphireCard",
        2,
        {
          bin: {
            id: Number(bin.id),
            bin: bin.bin,
            network: bin.network,
            topUpFee: Number(bin.topUpFee),
          },
          plans: plansByBin[Number(bin.id)],
        },
        false,
      );
    } else {
      openSheet(
        "createPlatinumCard",
        2,
        {
          bin: {
            id: Number(bin.id),
            bin: bin.bin,
            network: bin.network,
            plans: plansByBin[Number(bin.id)],
            topUpFee: Number(bin.topUpFee),
          },
        },
        false,
      );
    }
  };

  return (
    <div>
      <TopBar title="Back" backTo="/dashboard/cards" />

      <div className="px-4 pt-4 pb-8 lg:px-6 lg:pt-6">
        {/* Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-[#EBE8F308] px-4 py-4 shadow-sm sm:px-5 sm:py-5">
          <p className="text-[11px] tracking-wide text-[#9CA3AF] uppercase">
            Subscribe to
          </p>
          <h1 className="text-white mt-1 text-xl font-semibold sm:text-2xl">
            KrypKard
          </h1>
          <p className="text-dark-text-300 mt-1 text-xs leading-5 sm:text-sm">
            Get your virtual card and start spending crypto
          </p>
        </div>

        {/* Card grid */}
        <div className="mx-auto mt-4 grid max-w-[430px] gap-3 sm:mt-5 sm:gap-4 md:max-w-none lg:grid-cols-3">
          {isLoading ? (
            [1, 2, 3].map((i) => <CardSkeleton key={i} />)
          ) : (
            <>
              {((bins ?? []) as any[])
                // Only bins with a subscription package can be bought.
                .filter((b) => b.isActive && plansByBin[Number(b.id)])
                .sort(
                  (a, b) =>
                    PROVIDER_ORDER.indexOf(a.provider) -
                    PROVIDER_ORDER.indexOf(b.provider),
                )
                .map((bin, i) => {
                  const style = cardStyleFor(bin.provider, bin.network);
                  const { supportTitle, supportBody } = supportText(
                    bin.provider,
                  );
                  // Price and period come from the bin's plan only; the filter
                  // above guarantees at least one exists.
                  const plans = plansByBin[Number(bin.id)];
                  const period = plans.monthly ? "month" : "year";
                  const plan = (plans.monthly ?? plans.yearly)!;
                  const planPrices = [
                    Number(plan.price),
                    Number(plan.defaultPrice),
                  ];
                  const card: BinCardData = {
                    ...style,
                    id: bin.id,
                    name: "Virtual Card",
                    image: "",
                    price:
                      bin.provider !== "int"
                        ? Math.min(...planPrices)
                        : Number(plan.price),
                    defaultPrice:
                      bin.provider !== "int"
                        ? Math.max(...planPrices)
                        : undefined,
                    bin: bin.bin,
                    binValue: bin.bin,
                    network: bin.network,
                    cardType: bin.cardType,
                    provider: bin.provider,
                    needDepositForActiveCard: bin.needDepositForActiveCard,
                    needCardHolder: bin.needCardHolder,
                    minDepositAmount: Number(bin.minDepositAmount),
                    maxDepositAmount: Number(bin.maxDepositAmount),
                    topUpFee: Number(bin.topUpFee),
                  };

                  return (
                    <button
                      key={i}
                      onClick={() => handleClick(bin)}
                      className="block h-full w-full cursor-pointer text-left"
                    >
                      <div
                        className="group relative h-full rounded-2xl border border-[#3A3A3A] p-3 shadow-sm sm:p-4"
                        style={{
                          background:
                            "linear-gradient(129.49deg, rgba(27, 31, 53, 0.2) 3.6%, rgba(22, 25, 44, 0.2) 100%)",
                        }}
                      >
                        <BinCard
                          card={card}
                          className="rounded-2xl"
                          period={period}
                        />
                        <div className="mt-3 space-y-1.5">
                          <p className="text-[11px] text-[#9CA3AF]">
                            {supportTitle}
                          </p>
                          <p className="text-dark-text-300 min-h-10 text-[11px] leading-4 sm:min-h-12 sm:text-xs">
                            {supportBody}
                          </p>
                        </div>
                        <div className="mt-3 flex items-center justify-between pt-3 sm:mt-4">
                          <span className="text-white text-base font-semibold sm:text-lg">
                            Virtual Card
                          </span>
                          <ChevronRight className="text-[#F7F9FD] size-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </div>
                      </div>
                    </button>
                  );
                })}

              {/* Physical card — coming soon */}
              <div className="h-full">
                <div
                  className="group relative h-full rounded-2xl border border-[#3A3A3A] p-3 shadow-sm sm:p-4"
                  style={{
                    background:
                      "linear-gradient(129.49deg, rgba(27, 31, 53, 0.2) 3.6%, rgba(22, 25, 44, 0.2) 100%)",
                  }}
                >
                  <div className="h-[180px] overflow-hidden rounded-2xl bg-gradient-to-br from-slate-600 to-slate-800" />
                  <div className="mt-3 space-y-1.5">
                    <p className="text-[11px] text-[#9CA3AF]">
                      Supported Usage Scenarios
                    </p>
                    <p className="text-dark-text-300 min-h-10 text-[11px] leading-4 sm:min-h-12 sm:text-xs">
                      Everyday use for both online and offline purchase and ATM
                    </p>
                  </div>
                  <div className="mt-3 flex items-center justify-between pt-3 sm:mt-4">
                    <span className="text-white text-base font-semibold sm:text-lg">
                      Physical Card
                    </span>
                    <span className="rounded-full border border-[#E8EDF3] bg-[#F5F7FA] px-3 py-1 text-[10px] tracking-[0.18em] text-[#9CA3AF] uppercase">
                      Coming soon
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
